import { useCallback, useMemo } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { CallResult } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { PhoneRecord } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { UsersQueries } from '@frontend/api-users';
import { useTranslation } from '@frontend/i18n';
import { emitOpenCallInsightPanel, emitOpenVoicemailInsightPanel } from '@frontend/inbox-call-item-event-bus';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Avatar, Chat } from '@frontend/design-system';
import { ShareInMessageVirtuosoContext } from '../../types';

type CallBubbleProps = {
  callItem: PhoneRecord;
  context: ShareInMessageVirtuosoContext;
};

type BubbleInfo = {
  title: string;
  subtitle?: string;
  resultKey?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail';
};

export const CallBubble = ({ callItem, context: { personInThreadContext, person, optedOut } }: CallBubbleProps) => {
  const isInbound = callItem.direction === Direction.DIRECTION_INBOUND;

  const { t } = useTranslation('integrated-messaging');
  const { accessibleLocationIds } = useAppScopeStore();

  const users = UsersQueries.useGetMultiLocationsUsers({
    locationIds: accessibleLocationIds,
    params: { showChildren: true },
  }).flatMap(({ data }) => data || []);

  // personInThreadContext is for when thread body loads messages from different contact (custom number) while the header and sending context doesn't change.
  const inboundPatientName = personInThreadContext
    ? getFullName(personInThreadContext)
    : person
    ? getFullName(person)
    : undefined;

  const userSender = users.find((user) => user.UserID === callItem.createdBy);
  const userAvatarTitle = userSender ? getFullName(userSender) : undefined;
  const avatar = isInbound ? (
    <Photos.ContactProfilePhoto
      title={!inboundPatientName ? t('Unknown User') : undefined}
      disableClick
      personId={personInThreadContext?.personId ?? person?.personId ?? callItem.personId ?? ''}
      locationId={callItem.locationId}
      name={inboundPatientName || undefined}
      size='small'
    />
  ) : (
    <Avatar title={userAvatarTitle ?? t('Unknown User')} name={userAvatarTitle} size='small' isUser />
  );
  const timestamp = new Date(callItem.startedAt || '');
  const deleter = callItem.deletedAt ? users.find((user) => user.UserID === callItem.deletedBy) : undefined;
  const deletedBodyText = callItem.deletedAt
    ? t('This message was deleted on {{date}} by {{name}}', {
        date: new Date(callItem.deletedAt).toLocaleDateString(),
        name: deleter ? getFullName(deleter) : t('Unknown User'),
      })
    : undefined;

  const bubbleInfo: BubbleInfo = useMemo(() => {
    const callResult = callItem.callResult;

    switch (callResult) {
      case CallResult.CALL_RESULT_ANSWERED:
        return isInbound
          ? {
              title: t('Inbound Call'),
              subtitle: inboundPatientName || 'Anonymous user',
              resultKey: 'inbound',
            }
          : { title: t('Outbound Call'), resultKey: 'outbound' };
      case CallResult.CALL_RESULT_ABANDONED:
        return { title: t('Abandoned Call'), resultKey: 'abandoned' };
      case CallResult.CALL_RESULT_MISSED:
        return { title: t('Missed Call'), resultKey: 'missed' };
      case CallResult.CALL_RESULT_VOICEMAIL:
        return { title: t('Voicemail'), resultKey: 'voicemail' };
      default:
        return { title: t('Unknown Call') };
    }
  }, [callItem.callResult]);

  const isCallItemDeleted = callItem.deletedBy && callItem.deletedBy && deleter;

  const handleCallAction = useCallback(() => {
    if (bubbleInfo.resultKey === 'voicemail') {
      emitOpenVoicemailInsightPanel({
        voicemailId: callItem.channelId,
        locationId: callItem.locationId,
      });
    } else {
      emitOpenCallInsightPanel({
        channelId: callItem.channelId,
        includeCallIntelligence: true,
        locationId: callItem.locationId,
      });
    }
  }, [
    emitOpenCallInsightPanel,
    emitOpenVoicemailInsightPanel,
    bubbleInfo.resultKey,
    callItem.channelId,
    callItem.locationId,
  ]);

  return (
    <Chat
      direction={isInbound ? 'inbound' : 'outbound'}
      avatar={avatar}
      timestamp={timestamp}
      css={{
        padding: theme.spacing(0, 2),
        opacity: optedOut ? 0.5 : 1,
      }}
    >
      {isCallItemDeleted ? (
        <Chat.Bubble variant={'deleted'} text={deletedBodyText} />
      ) : (
        <Chat.Bubble
          variant='call'
          callResult={bubbleInfo.resultKey}
          callProps={{
            callResult: bubbleInfo.resultKey,
            callAction: handleCallAction,
          }}
          text={bubbleInfo.title}
          subText={bubbleInfo.subtitle}
          key={callItem.channelId}
          maxWidth={202}
          styles={{
            backgroundColor: theme.colors.white,
            borderRadius: theme.spacing(1),
            outline: `0.50px solid ${theme.colors.neutral20}`,
            '&:hover': {
              backgroundColor: theme.colors.neutral5,
            },
          }}
        />
      )}
    </Chat>
  );
};
